import { useEffect, useRef, useState } from 'react';
import { useInternationalization } from '@progress/kendo-react-intl';
import { useSelector } from 'react-redux';
import { RootState } from '../../../../../redux/store';
import { useParams } from 'react-router-dom';

import { IGridPreparedData } from '../../../types/IGridPreparedData';
import { ISetup } from '../../../types/ISetup';
import { IGridDefinition } from '../../../types/IGridDefinition';
import { ICurrency } from '../../../types/ICurrency';
import { IWidget } from '../../../../../common/services/http/types/IWidget';
import { AnalysisContentWidgetModel } from '../../../../../models/AnalysisContentWidgetModel';
import { IMeasure } from '../../../types/IMeasure';
import { INote, IWidgetCombinedData } from '../../../../../common/services/http/analysisContentService';
import { IStaticProperty } from '../../../../../models/types/IStaticProperty';
import { AnalysisContentWidgetDataModel } from '../../../../../models/AnalysisContentWidgetDataModel';

const useGetGridData = (setup: ISetup, widget: AnalysisContentWidgetModel | IWidget, measures: IMeasure[], notes: INote[], staticProperties: IStaticProperty[], scenarios, selectedObject, selectedPeriod, boardSelectedCurrency, investorObjectId) => {
  const [selectedGrid, setSelectedGrid] = useState<number>(0);
  const [sectionsForGrid, setSectionsForGrid] = useState<string[]>([]);
  const [gridContentEnriched, setGridContentEnriched] = useState<any[]>([]);
  const [selectedCurrency, setSelectedCurrency] = useState<ICurrency[]>([]);
  const [preparedData, setPreparedData] = useState<IGridPreparedData[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [gridNotes, setGridNotes] = useState<INote[]>([]);
  const [currencies, setCurrencies] = useState<ICurrency[]>([]);
  const [measureHash, setMeasureHash] = useState<any>({});
  const [staticPropertyHash, setStaticPropertyHash] = useState<any>({});
  const [objectValueMap, setObjectValueMap] = useState<any>({});
  const intl = useInternationalization();
  const widgetDataRef = useRef(null);

  const investor = useSelector((state: RootState) => state.investor.investor);
  const { investorId } = useParams();

  const _measureHash = {};
  const _propertyHash = {};

  let _objectValueMap = {};
  let tempGridNotesArray: INote[] = [];

  const extractGridSectionsFromSetup = (): void => {
    let gridSectionArray: string[] = [];
    setup.definitions.forEach((definition: IGridDefinition) => {
      gridSectionArray.push(definition.name);
    });
    setSectionsForGrid(gridSectionArray);
  };

  const generateColumnsForGridSection = (): void => {
    if (setup.singlePeriod) {
      setGridContentEnriched(setup.definitions[selectedGrid].columns);
    }
    else {
      let columns = [];
      let totalColumns = [];

      widgetDataRef.current.nodes.forEach((node) => {
        setup.definitions[selectedGrid].columns.forEach((column) => {
          let measure = measures.find((item) => item.id === column.measureId);
          if (measure) {
            columns.push({ ...column, nodeId: node.id, nodeName: node.name, format: measure.isPercent ? 'p' : 'n' });

            if (column.total && !totalColumns.find((item) => item.measureId === column.measureId))
              totalColumns.push({ ...column, isTotal: true, format: measure.isPercent ? 'p' : 'n' });
          }
        });
      });

      totalColumns.forEach((totalColumn) => {
        columns.push(totalColumn);
      });
      setGridContentEnriched(columns);
    }
  };

  const filterNotesForGrid = (notes: INote[]): void => {
    let currentColumns = setup.definitions[selectedGrid].columns;
    if (notes) {
      for (let i = 0; i < notes.length; i++) {
        for (let j = 0; j < notes[i].measures.length; j++) {
          let tempNoteMeasureId = notes[i].measures[j].measureId;
          currentColumns.map((el) => {
            if (el.measureId === tempNoteMeasureId && !tempGridNotesArray.includes(notes[i])) {
              tempGridNotesArray.push(notes[i]);
            }
          });
        }
      }
    }

    setGridNotes(tempGridNotesArray);
    if (notes && setup.singlePeriod)
      extractNotesForGridItems();
  };

  const extractNotesForGridItems = (): any => {
    let currentColumns = setup.definitions[selectedGrid].columns;
    let copiedGridContent = currentColumns.map((object) => ({ ...object }));
    for (let i = 0; i < currentColumns.length; i++) {
      let tempItemNotes: INote[] = [];
      for (let j = 0; j < notes.length; j++) {
        let tempNoteMeasures = notes[j].measures.map((el) => el.measureId);
        if (tempNoteMeasures.includes(currentColumns[i].measureId)) {
          tempItemNotes.push(notes[j]);
          copiedGridContent[i].hasNotes = true;
          copiedGridContent[i].notes = tempItemNotes;
          setGridContentEnriched(copiedGridContent);
        }
      }
    }
  };

  const _createMeasureHash = (): void => {
    if (measures) {
      for (let i = 0; i < measures.length; i++) {
        let measure = measures[i];
        _measureHash[measure.id] = measure;
      }
    }
    if (staticProperties) {
      staticProperties.forEach((templateProp) => {
        _propertyHash[templateProp.id] = templateProp;
      });
    }
    setMeasureHash(_measureHash);
    setStaticPropertyHash(_propertyHash);
  };

  const prepareData = (fetchedData: IWidgetCombinedData, nodes: any): IGridPreparedData[] => {
    let preparedData: IGridPreparedData[] = [];
    _objectValueMap = {};

    fetchedData.measureValues.forEach((row) => {
      let measure = _measureHash[row.measureId];
      if (measure) {
        let node = nodes.find(function(item) {
          return item.id === row.nodeId;
        });
        if (node) {
          let field = fieldNameFor('measure', measure.id);
          let objectValue = prepRow(preparedData, node, row, field, 'amount');
          if (objectValue) {
            objectValue.row[field] = objectValue.row[field] === null ? 'N/A' : intl.toString(objectValue.row[field], (measure.isPercent ? 'p' : 'n') + measure.scale);
            _createDataValueInfo(objectValue, row, node, measure);
          }
        }
      }
    });

    if (fetchedData.staticValues) {
      fetchedData.staticValues.forEach(function(staticRow) {
        let property = _propertyHash[staticRow.templatePropertyId];
        if (property) {
          let node = nodes.find(function(item) {
            return item.id === staticRow.nodeId;
          });
          if (node) {
            let field = fieldNameFor('property', property.id);
            prepRow(preparedData, node, staticRow, field, 'rawValue');
          }
        }
      });
    }
    setObjectValueMap(_objectValueMap);
    return preparedData;
  };

  const prepareDataHistorical = (fetchedData: IWidgetCombinedData, nodes: any) : IGridPreparedData[] => {
    let preparedData : IGridPreparedData[] = [];
    _objectValueMap = {};

    fetchedData.measureValues.forEach(function (row) {
      let measure = _measureHash[row.measureId];
      if (measure) {
        let node = nodes.find(function (item) { return item.id === row.nodeId; });
        if (node) {
          let field = fieldNameFor('measure', measure.id, node.id);
          let objectValue = prepRowHistorical(preparedData, row, field, 'amount');

          if (objectValue) {
            let amount = objectValue.row[field];
            objectValue.row[field] = amount === null ? 'N/A' : intl.toString(amount, (measure.isPercent ? 'p' : 'n') + measure.scale);
            let key = measure.id + '_' + node.id;
            objectValue.map[key] = {
              nodeId: node.id,
              scenarioId: node.scenarioId,
              valueId: row.id,
              periodId: row.periodId
            };

            let totalField = fieldNameFor('measure', measure.id);
            let totalAmt = objectValue.row[totalField];
            objectValue.row[totalField] = (totalAmt || 0) + (amount || 0);
          }
        }
      }
    });

    setObjectValueMap(_objectValueMap);
    return preparedData;
  };

  const prepRowHistorical = (preparedData, row, field, valueField) => {
    let objectValue = _objectValueMap[row['periodId']];
    if (objectValue) {
      objectValue.row[field] = row[valueField];
    } else {
      objectValue = {
        row: { objectId: row.periodId, name: row.periodName, startDate: row.startDate, endDate: row.endDate },
        map: {}
      };
      objectValue.row[field] = row[valueField];

      _objectValueMap[row['periodId']] = objectValue;
      preparedData.push(objectValue.row);
    }
    return objectValue;
  };

  const prepRow = (preparedData, node, row, field, valueField) => {
    let objectValue = _objectValueMap[row['objectId']];
    if (objectValue) {
      objectValue.row[field] = row[valueField];
    } else {
      objectValue = {
        row: {
          objectId: row.objectId,
          name: node.name,
          nodeId: row.nodeId,
          parentNodeId: row.parentNodeId,
          currency: node.currency,
        },
        map: {},
      };
      objectValue.row[field] = row[valueField];

      _objectValueMap[row['objectId']] = objectValue;
      preparedData.push(objectValue.row);
    }
    return objectValue;
  };

  const fieldNameFor = (type: string, id: number, nodeId?: number): string => {
    return type + '_' + id + (nodeId ? '_' + nodeId : '');
  };

  const _createDataValueInfo = (objectValue: any, dataRow: any, node: any, measure: IMeasure): void => {
    objectValue.map[measure.id] = {
      nodeId: node.id,
      scenarioId: node.scenarioId,
      valueId: dataRow.id,
    };
  };

  const exportGrid = useRef(null);

  const gridExport = (): void => {
    if (exportGrid.current !== null) {
      exportGrid.current.save();
    }
  };

  const selectGridSection = (id: number): void => {
    setSelectedGrid(id);
  };

  const measuresForFetch = () => {
    let measFetch = [];
    let hash = {};
    widget.setup.definitions.forEach(function(definition) {
      for (let i = 0; i < definition.columns.length; i++) {
        let measureId = definition.columns[i].measureId;
        if (!measureId || hash[measureId]) continue;
        measFetch.push({ measureId: measureId });
        hash[measureId] = true;
      }
    });
    return measFetch;
  };

  const propertiesForFetch = () => {
    let properties = [];
    let hash = {};
    setup.definitions.forEach(function(definition) {
      for (let i = 0; i < definition.columns.length; i++) {
        let templatePropertyId = definition.columns[i].templatePropertyId;
        if (!templatePropertyId || hash[templatePropertyId]) continue;
        properties.push({ templatePropertyId: templatePropertyId });
        hash[templatePropertyId] = true;
      }
    });
    return properties;
  };

  const handleCurrencyChange = (e) => {
    setSelectedCurrency(e.value);
  };

  const handleFindSelection = (currency, arrayToFind) => {
    if (arrayToFind.find((curr) => curr.currency === currency.currency)) {
      return currency;
    } else {
      return false;
    }
  };

  useEffect(() => {
    widgetDataRef.current = new AnalysisContentWidgetDataModel(widget, measuresForFetch(), investorId ? parseInt(investorId) : parseInt(investorObjectId), false, propertiesForFetch());
    widgetDataRef.current.defineNodes(selectedObject);
  }, [investor]);

  useEffect(() => {
    filterNotesForGrid(notes);
  }, [notes]);

  useEffect(() => {
    if (boardSelectedCurrency) {
      let currency = handleFindSelection(boardSelectedCurrency, widgetDataRef.current.generateCurrencies(scenarios));
      if (currency) {
        setSelectedCurrency(currency);
      }
    }
  }, [boardSelectedCurrency]);

  useEffect(() => {
    //@ts-ignore
    if (selectedCurrency.currency) {
      extractGridSectionsFromSetup();
      generateColumnsForGridSection();
      _createMeasureHash();
      widgetDataRef.current.defineNodes(selectedObject);
      setCurrencies(widgetDataRef.current.generateCurrencies(scenarios));
      //@ts-ignore
      widgetDataRef.current.fetchCombinedData(selectedPeriod, selectedCurrency.currency).then((res: IWidgetCombinedData) => {
        setPreparedData(setup.singlePeriod ?
          prepareData(res, widgetDataRef.current.nodes) :
          prepareDataHistorical(res, widgetDataRef.current.nodes));

        setLoading(false);
      });
    }
  }, [selectedObject, selectedGrid, selectedPeriod, selectedCurrency]);

  return {
    sectionsForGrid,
    gridContentEnriched,
    preparedData,
    loading,
    gridNotes,
    measureHash,
    staticPropertyHash,
    objectValueMap,
    gridExport,
    exportGrid,
    selectGridSection,
    currencies,
    selectedCurrency,
    handleCurrencyChange,
  };
};

export default useGetGridData;
